/**
 * ADM-03 学生详情浮层（student-registry-admin-ui:SC-05/SC-06/SC-07/SC-08/SC-15/SC-16/SC-17，
 * design 前端页面变更；班级/课表/账本三 Tab 由 student-detail-cross-domain-tabs 回填）。
 *
 * 标题行：姓名 + 性别/学段/状态 Tag + 赠送课时按钮（ADM-02 GRANT 双闸，manual-trial-grant-admin-ui:SC-01，
 * 成功后经 refreshToken 刷新账本 Tab）+ 编辑按钮 + 归档/恢复 Popconfirm 二次确认。
 * 档案信息 Descriptions：学号/校区/创建时间/归档时间/标签/科目（解析名称）/科目分数
 * （student_subject_scores 逐科展示，按科目名排序；不再有单值分数）/监护人称呼/
 * 与孩子关系/共用手机号/手机号变更时间/介绍人/分享来源/关联商品/注册时间。
 * 归因空值兜底为「无」。五 Tab 全部接真实数据：报名/订单为既有列表；
 * 班级（含 ENDED 历史）/课表（今天−7~+14 窗口）/账本（摘要+流水）为跨域只读摘要。
 */
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Descriptions, Drawer, Empty, Popconfirm, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';

const { Text } = Typography;
import { EditOutlined, GiftOutlined } from "@ant-design/icons";
import {
  studentsApi,
  type GuardianRelationship,
  type ShareSourcePage,
  type StudentDetail,
  type StudentStatus,
} from "../../api/students.api";
import { subjectsApi } from "../../api/subjects.api";
import {
  enrollmentsApi,
  CONVERSION_STATUS_LABEL,
  type EnrollmentItem,
} from "../../api/enrollments.api";
import {
  billingApi,
  ORDER_STATUS_LABEL,
  type OrderListItem,
} from "../../api/billing.api";
import { classesApi, type StudentClassView } from "../../api/classes.api";
import {
  listLessonSessions,
  type LessonSessionListItem,
} from "../../api/scheduling.api";
import {
  entitlementLedgerApi,
  type LedgerEntryDetail,
  type LedgerEntryListItem,
  type StudentAccountSummary,
} from "../../api/entitlementLedger.api";
import { useHasPermission } from "../../auth/HasPermission";
import { useSession } from "../../auth/SessionContext";
import { ApiError } from "../../api/transport";
import { genderLabel, gradeStageLabel } from "../../api/labels";
import { fmtDateTime } from "../../utils/datetime";
import { MaskedMobile } from "./MaskedMobile";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { ManualTrialGrantModal } from "./ManualTrialGrantModal";
import { RefundRecordDialog } from "../billing/RefundRecordDialog";
import { EnrollmentStatusTag } from "../classes/ClassStatusTag";
import { SessionStatusTag } from "../classes/ScheduleWorkbenchPage";
import {
  LedgerDetailDrawer,
  ledgerBusinessRefLabel,
  ledgerTypeLabel,
} from "../deduction/LedgerDetailDrawer";

const RELATIONSHIP_LABEL: Record<GuardianRelationship, string> = {
  FATHER: "父亲",
  MOTHER: "母亲",
  GRANDPA: "爷爷",
  GRANDMA: "奶奶",
  OTHER: "其他",
};

const SHARE_PAGE_LABEL: Record<ShareSourcePage, string> = {
  HOME: "首页",
  PRODUCT_LIST: "商品列表",
  PRODUCT_DETAIL: "商品详情",
  SCHEDULE: "课表",
  ENTITLEMENT: "权益",
  PROFILE: "我的",
};

function StatusTag({ status }: { status: StudentStatus }) {
  return status === "STUDYING" ? (
    <Tag color="green">在读</Tag>
  ) : (
    <Tag color="default">已归档</Tag>
  );
}

function emptyText(value: string | null | undefined): string {
  return value ? value : "无";
}

function EnrollmentTab({ studentId }: { studentId: string }) {
  const [items, setItems] = useState<EnrollmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const r = await enrollmentsApi.listEnrollments({
          studentId,
          page: 1,
          pageSize: 100,
        });
        if (!cancelled)
          setItems(r.items.filter((i) => i.studentId === studentId));
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);
  return (
    <Table<EnrollmentItem>
      size="small"
      rowKey="applicationId"
      loading={loading}
      dataSource={items}
      locale={{
        emptyText: (
          <Empty description="暂无报名记录" styles={{ image: { height: 40 } }} />
        ),
      }}
      pagination={false}
      columns={[
        { title: "课程SKU", dataIndex: "skuName" },
        {
          title: "转化状态",
          dataIndex: "conversionStatus",
          render: (v: EnrollmentItem["conversionStatus"]) =>
            CONVERSION_STATUS_LABEL[v] ?? v,
        },
        { title: "校区", dataIndex: "campusName" },
        {
          title: "提交时间",
          dataIndex: "submittedAt",
          render: (v: string) => fmtDateTime(v),
        },
      ]}
    />
  );
}

function OrderTab({ studentId, studentName }: { studentId: string; studentName: string | null }) {
  const session = useSession();
  const staffName = session.session?.name ?? null;
  const [items, setItems] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadSeed, setReloadSeed] = useState(0);
  // 登记退款双闸（order-manual-refund-admin-ui:SC-01/SC-02）：无 REFUND 权限不渲染入口
  const canRefund = useHasPermission("ADM-09", "REFUND");
  const [refundTarget, setRefundTarget] = useState<OrderListItem | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const r = await billingApi.listOrders({
          studentId,
          page: 1,
          pageSize: 100,
        });
        if (!cancelled) setItems(r.items);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, reloadSeed]);
  return (
    <>
      <Table<OrderListItem>
        size="small"
        rowKey="orderId"
        loading={loading}
        dataSource={items}
        locale={{
          emptyText: <Empty description="暂无订单" styles={{ image: { height: 40 } }} />,
        }}
        pagination={false}
        columns={[
          { title: "订单号", dataIndex: "orderNo" },
          { title: "金额", dataIndex: "receivableAmount", width: 100, render: (v: number) => <MoneyDisplay amountInCents={v} /> },
          {
            // 实收口径五处同源（order-manual-refund-admin-ui:SC-06/SC-07）：服务端派生减退款净值；有退款红色强调（原型）
            title: "实收",
            dataIndex: "netReceivedAmount",
            render: (value: number, record) => (
              <span data-testid="student-order-net-received">
                {record.refundedAmount > 0 ? (
                  <Text type="danger">
                    <MoneyDisplay amountInCents={value} />
                  </Text>
                ) : (
                  <MoneyDisplay amountInCents={value} />
                )}
              </span>
            ),
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 90,
            render: (v: OrderListItem["status"]) => ORDER_STATUS_LABEL[v] ?? v,
          },
          {
            title: "创建时间",
            dataIndex: "createdAt",
            width: 110,
            render: (v: string) => (v ? v.slice(0, 10) : "-"),
          },
          ...(canRefund
            ? [
                {
                  title: "操作",
                  width: 90,
                  render: (_: unknown, record: OrderListItem) =>
                    record.netReceivedAmount > 0 ? (
                      <Button
                        size="small"
                        danger
                        type="link"
                        onClick={() => setRefundTarget(record)}
                        data-testid={`student-order-refund-${record.orderNo}`}
                      >
                        登记退款
                      </Button>
                    ) : null,
                },
              ]
            : []),
        ]}
      />
      {refundTarget && (
        <RefundRecordDialog
          open
          orderId={refundTarget.orderId}
          orderNo={refundTarget.orderNo}
          studentName={studentName ?? ""}
          paidAmountMinor={refundTarget.paidAmount}
          netReceivedAmountMinor={refundTarget.netReceivedAmount}
          refundedAmountMinor={refundTarget.refundedAmount}
          orderStatusLabel={ORDER_STATUS_LABEL[refundTarget.status] ?? refundTarget.status}
          operatorName={staffName ?? ""}
          onClose={() => setRefundTarget(null)}
          onRefunded={() => setReloadSeed((seed) => seed + 1)}
        />
      )}
    </>
  );
}


/** 班级 Tab：按学生查班级（含 ENDED 历史班级，SC-15；与 ADM-16 成员口径一致）。 */
function ClassTab({ studentId }: { studentId: string }) {
  const [items, setItems] = useState<StudentClassView[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const r = await classesApi.listClassesByStudent(studentId);
        if (!cancelled) setItems(r);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);
  return (
    <Table<StudentClassView>
      size="small"
      rowKey="classId"
      loading={loading}
      dataSource={items}
      locale={{
        emptyText: (
          <Empty description="暂无班级记录" styles={{ image: { height: 40 } }} />
        ),
      }}
      pagination={false}
      columns={[
        { title: "班级", dataIndex: "className" },
        { title: "课程SKU", dataIndex: "skuName" },
        {
          title: "教师",
          dataIndex: "teacherName",
          render: (v: string | null) => v ?? "—",
        },
        { title: "校区", dataIndex: "campusName" },
        {
          title: "入班时间",
          dataIndex: "joinedAt",
          render: (v: string) => fmtDateTime(v),
        },
        {
          title: "离班时间",
          dataIndex: "leftAt",
          render: (v: string | null) => (v ? fmtDateTime(v) : "—"),
        },
        {
          title: "在班状态",
          dataIndex: "enrollmentStatus",
          render: (v: StudentClassView["enrollmentStatus"]) => (
            <EnrollmentStatusTag status={v} />
          ),
        },
      ]}
    />
  );
}

/** 课表 Tab：默认窗口今天−7 天 ~ +14 天（SC-16；已取消课次带标识）。 */
function ScheduleTab({ studentId }: { studentId: string }) {
  const [items, setItems] = useState<LessonSessionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const from = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        const to = dayjs().add(14, "day").format("YYYY-MM-DD");
        const r = await listLessonSessions(from, to, studentId);
        if (!cancelled) setItems(r);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);
  return (
    <Table<LessonSessionListItem>
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={items}
      locale={{
        emptyText: (
          <Empty description="窗口内暂无课次" styles={{ image: { height: 40 } }} />
        ),
      }}
      pagination={false}
      columns={[
        { title: "日期", dataIndex: "sessionDate" },
        {
          title: "时间",
          dataIndex: "startAt",
          render: (v: string, row) =>
            `${dayjs(v).format("HH:mm")}~${dayjs(row.endAt).format("HH:mm")}`,
        },
        { title: "班级", dataIndex: "className" },
        { title: "教室", dataIndex: "classroomName" },
        {
          title: "状态",
          dataIndex: "status",
          render: (v: LessonSessionListItem["status"]) => (
            <SessionStatusTag status={v} />
          ),
        },
      ]}
    />
  );
}

const LEDGER_FORBIDDEN_TEXT = "当前角色无账本查看权限";
const LEDGER_PAGE_SIZE = 5;

/** 账户汇总行：账户摘要 + 该账户最新一条流水的更新时间/事由（对照 !70 原型两表结构）。 */
interface AccountRow extends StudentAccountSummary {
  latestAt: string | null;
  latestReason: string;
}

/**
 * 账本 Tab（SC-17，对照 !70 原型两表）：上半账户汇总（账户/购买课时/赠送课时/可用课时/
 * 更新时间/更新事由）+ 下半流水明细（时间/变动事由/赠送与购买课时前→后/可用前→后/业务来源）。
 * 更新时间/事由由已加载流水按账户取最新（翻页累积合并）；行点击开流水详情抽屉。
 * 权限双闸：ADM-24 VIEW 预判不发请求；请求 403（校区数据范围）兜底同空态。
 * refreshToken 变化触发重查（赠送课时成功后刷新余额与流水）。
 */
function LedgerTab({
  studentId,
  refreshToken,
}: {
  studentId: string;
  refreshToken: number;
}) {
  const { permissionsLoading } = useSession();
  const hasLedgerPermission = useHasPermission("ADM-24", "VIEW");
  const [accounts, setAccounts] = useState<StudentAccountSummary[]>([]);
  const [entries, setEntries] = useState<LedgerEntryListItem[]>([]);
  const [latestByAccount, setLatestByAccount] = useState<
    Record<string, LedgerEntryListItem>
  >({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [entryDetail, setEntryDetail] = useState<LedgerEntryDetail | null>(
    null,
  );

  /** 将一页流水合并进「每账户最新流水」索引（列表按时间倒序，仅补缺失账户）。 */
  function mergeLatest(items: LedgerEntryListItem[]): void {
    setLatestByAccount((prev) => {
      const next = { ...prev };
      for (const e of items) {
        const seen = next[e.accountId];
        if (!seen || seen.createdAt < e.createdAt) next[e.accountId] = e;
      }
      return next;
    });
  }

  useEffect(() => {
    if (permissionsLoading || !hasLedgerPermission || !studentId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [accountItems, entryPage] = await Promise.all([
          entitlementLedgerApi.getStudentAccounts(studentId),
          entitlementLedgerApi.listEntries({
            studentId,
            page: 1,
            pageSize: LEDGER_PAGE_SIZE,
          }),
        ]);
        if (!cancelled) {
          setForbidden(false);
          setAccounts(accountItems);
          setEntries(entryPage.items);
          setTotal(entryPage.total);
          setPage(1);
          setLatestByAccount({});
          mergeLatest(entryPage.items);
        }
      } catch (err) {
        if (cancelled) return;
        // 403（含 entitlement.ledger.campus_forbidden）：无权访问，空态兜底而非静默空
        if (err instanceof ApiError && err.status === 403) setForbidden(true);
        else {
          setAccounts([]);
          setEntries([]);
          setTotal(0);
          setLatestByAccount({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, hasLedgerPermission, permissionsLoading, refreshToken]);

  async function loadEntryPage(nextPage: number): Promise<void> {
    setLoading(true);
    try {
      const r = await entitlementLedgerApi.listEntries({
        studentId,
        page: nextPage,
        pageSize: LEDGER_PAGE_SIZE,
      });
      setEntries(r.items);
      setTotal(r.total);
      setPage(nextPage);
      mergeLatest(r.items);
    } catch {
      /* 翻页失败保留当前页 */
    } finally {
      setLoading(false);
    }
  }

  async function openEntryDetail(entryId: string): Promise<void> {
    try {
      setEntryDetail(await entitlementLedgerApi.getEntry(entryId));
    } catch {
      setEntryDetail(null);
    }
  }

  if (permissionsLoading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    );
  }
  if (!hasLedgerPermission || forbidden) {
    return (
      <Empty description={LEDGER_FORBIDDEN_TEXT} styles={{ image: { height: 40 } }} />
    );
  }

  const accountRows: AccountRow[] = accounts.map((a) => {
    const latest = latestByAccount[a.accountId];
    return {
      ...a,
      latestAt: latest?.createdAt ?? null,
      latestReason: latest
        ? (ledgerTypeLabel[latest.type] ?? latest.type)
        : "—",
    };
  });

  return (
    <>
      <Table<AccountRow>
        size="small"
        rowKey="accountId"
        loading={loading}
        dataSource={accountRows}
        locale={{
          emptyText: (
            <Empty description="暂无课时账户" styles={{ image: { height: 40 } }} />
          ),
        }}
        pagination={false}
        columns={[
          { title: "账户（课程SKU）", dataIndex: "skuName" },
          { title: "购买课时", dataIndex: "formalAvailable" },
          { title: "赠送课时", dataIndex: "giftedAvailable" },
          {
            title: "可用课时",
            render: (_, a) => a.formalAvailable + a.giftedAvailable,
          },
          {
            title: "更新时间",
            dataIndex: "latestAt",
            render: (v: string | null) => (v ? fmtDateTime(v) : "—"),
          },
          { title: "更新事由", dataIndex: "latestReason" },
        ]}
      />
      <Table<LedgerEntryListItem>
        style={{ marginTop: 12 }}
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={entries}
        locale={{
          emptyText: (
            <Empty description="暂无课时流水" styles={{ image: { height: 40 } }} />
          ),
        }}
        pagination={{
          current: page,
          pageSize: LEDGER_PAGE_SIZE,
          total,
          size: "small",
          showSizeChanger: false,
          onChange: (p) => void loadEntryPage(p),
        }}
        onRow={(record) => ({ onClick: () => void openEntryDetail(record.id) })}
        columns={[
          {
            title: "时间",
            dataIndex: "createdAt",
            render: (v: string) => fmtDateTime(v),
          },
          {
            title: "变动事由",
            dataIndex: "type",
            render: (v: LedgerEntryListItem["type"]) => ledgerTypeLabel[v] ?? v,
          },
          {
            title: "赠送课时（前→后）",
            render: (_, e) => `${e.giftedBefore} → ${e.giftedAfter}`,
          },
          {
            title: "购买课时（前→后）",
            render: (_, e) => `${e.formalBefore} → ${e.formalAfter}`,
          },
          {
            title: "可用课时（前→后）",
            render: (_, e) =>
              `${e.giftedBefore + e.formalBefore} → ${e.giftedAfter + e.formalAfter}`,
          },
          {
            title: "业务来源",
            render: (_, e) => (
              <a onClick={() => void openEntryDetail(e.id)}>
                {ledgerBusinessRefLabel[e.businessRefType] ?? e.businessRefType}{" "}
                · {e.businessRefId}
              </a>
            ),
          },
        ]}
      />
      <LedgerDetailDrawer
        detail={entryDetail}
        onClose={() => setEntryDetail(null)}
      />
    </>
  );
}

export function StudentDetailDrawer({
  studentId,
  open,
  onClose,
  onEdit,
  onChanged,
}: {
  studentId: string | null;
  open: boolean;
  onClose: () => void;
  onEdit: (student: StudentDetail) => void;
  onChanged?: () => void;
}) {
  const [detail, setDetail] = useState<StudentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [subjectNames, setSubjectNames] = useState<string[]>([]);
  // 赠送课时入口权限双闸（SC-01）：无 ADM-02 GRANT 不渲染按钮（服务端仍强制 403）
  const canGrantTrial = useHasPermission("ADM-02", "GRANT");
  const canEditStudent = useHasPermission("ADM-02", "EDIT");
  const [grantOpen, setGrantOpen] = useState(false);
  /** 账本 Tab 刷新令牌：赠送成功后自增触发 LedgerTab 重查。 */
  const [ledgerRefreshToken, setLedgerRefreshToken] = useState(0);

  async function loadDetail() {
    if (!studentId) return;
    setLoading(true);
    try {
      const d = await studentsApi.getStudent(studentId);
      setDetail(d);
      if (d.subjectIds?.length) {
        const page = await subjectsApi.listSubjects({ page: 1, pageSize: 100 });
        setSubjectNames(
          d.subjectIds
            .map((id) => page.items.find((s) => s.id === id)?.name)
            .filter((n): n is string => !!n),
        );
      } else {
        setSubjectNames([]);
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : "加载详情失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && studentId) void loadDetail();
  }, [open, studentId]);

  async function handleArchive() {
    if (!detail) return;
    try {
      await studentsApi.archiveStudent(detail.id);
      message.success("已归档");
      void loadDetail();
      onChanged?.();
    } catch (err) {
      message.error(err instanceof Error ? err.message : "归档失败");
    }
  }

  async function handleRestore() {
    if (!detail) return;
    try {
      await studentsApi.restoreStudent(detail.id);
      message.success("已恢复");
      void loadDetail();
      onChanged?.();
    } catch (err) {
      message.error(err instanceof Error ? err.message : "恢复失败");
    }
  }

  const primaryGuardian = detail?.guardians?.[0];

  return (
    <Drawer
      title={
        detail ? (
          <Space wrap>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{detail.name}</span>
            <Tag>{genderLabel[detail.gender] ?? detail.gender}</Tag>
            <Tag>{gradeStageLabel[detail.gradeStage] ?? detail.gradeStage}</Tag>
            <StatusTag status={detail.status} />
          </Space>
        ) : (
          "学生详情"
        )
      }
      open={open}
      onClose={onClose}
      width={720}
      loading={loading}
      extra={
        detail && (
          <Space>
            {canGrantTrial && (
              <Button
                icon={<GiftOutlined />}
                onClick={() => setGrantOpen(true)}
                data-testid="btn-grant-trial"
              >
                赠送课时
              </Button>
            )}
            {canEditStudent && (
              <Button
                icon={<EditOutlined />}
                onClick={() => onEdit(detail)}
                data-testid="btn-edit-student"
              >
                编辑
              </Button>
            )}
            {canEditStudent &&
              (detail.status === "STUDYING" ? (
                <Popconfirm
                  title="确认归档该学生？"
                  description="归档后不再参与新分班和排课，历史记录仍可查询。"
                  onConfirm={handleArchive}
                  okText="归档"
                  cancelText="取消"
                >
                  <Button danger data-testid="btn-archive-student">
                    归档
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="确认恢复该学生？"
                  description="恢复为在读后方可继续参与分班和排课。"
                  onConfirm={handleRestore}
                  okText="恢复"
                  cancelText="取消"
                >
                  <Button data-testid="btn-restore-student">恢复</Button>
                </Popconfirm>
              ))}
          </Space>
        )
      }
    >
      {detail && (
        <>
          <Descriptions
            column={2}
            bordered
            size="small"
            data-testid="detail-descriptions"
          >
            <Descriptions.Item label="学号">
              {detail.studentNo}
            </Descriptions.Item>
            <Descriptions.Item label="归属校区">
              {detail.homeCampusName ?? "无"}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {new Date(detail.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="归档时间">
              {detail.archivedAt
                ? new Date(detail.archivedAt).toLocaleString()
                : "无"}
            </Descriptions.Item>
            <Descriptions.Item label="标签" span={2}>
              {detail.tags?.length ? (
                detail.tags.map((t) => <Tag key={t}>{t}</Tag>)
              ) : (
                <span style={{ color: "#999" }}>暂无标签，可在编辑中维护</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="科目" span={2}>
              {subjectNames.length ? (
                subjectNames.join("、")
              ) : detail.subjectIds?.length ? (
                `${detail.subjectIds.length} 项`
              ) : (
                <span style={{ color: "#999" }}>无</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="科目分数" span={2}>
              {detail.subjectScores?.length ? (
                <div data-testid="detail-subject-scores">
                  {[...detail.subjectScores]
                    .sort((a, b) =>
                      a.subjectName.localeCompare(b.subjectName, "zh"),
                    )
                    .map((s) => (
                      <div
                        key={s.subjectId}
                      >{`${s.subjectName}:${s.score}`}</div>
                    ))}
                </div>
              ) : (
                <span style={{ color: "#999" }}>暂无科目分数</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="监护人称呼">
              {primaryGuardian?.name ?? "无"}
            </Descriptions.Item>
            <Descriptions.Item label="与孩子关系">
              {primaryGuardian
                ? (RELATIONSHIP_LABEL[primaryGuardian.relationship] ??
                  primaryGuardian.relationship)
                : "无"}
            </Descriptions.Item>
            <Descriptions.Item label="共用手机号" span={2}>
              <MaskedMobile
                studentId={detail.id}
                masked={detail.guardianMobile}
              />
            </Descriptions.Item>
            <Descriptions.Item label="手机号变更时间（系统只读）">
              {detail.guardianMobileChangedAt
                ? new Date(detail.guardianMobileChangedAt).toLocaleString()
                : "无"}
            </Descriptions.Item>
            <Descriptions.Item label="注册时间">
              <span data-testid="detail-referrer-registered-at">
                {detail.referrerRegisteredAt
                  ? new Date(detail.referrerRegisteredAt).toLocaleString()
                  : "无"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="介绍人">
              <span data-testid="detail-referrer-display-name">
                {emptyText(detail.referrerDisplayName)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="分享来源">
              <span data-testid="detail-share-source-page">
                {detail.shareSourcePage
                  ? (SHARE_PAGE_LABEL[detail.shareSourcePage] ??
                    detail.shareSourcePage)
                  : "无"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="关联商品">
              <span data-testid="detail-share-product-name">
                {emptyText(detail.shareProductName)}
              </span>
            </Descriptions.Item>
          </Descriptions>
          <Tabs
            style={{ marginTop: 16 }}
            items={[
              {
                key: "enrollment",
                label: "报名",
                children: <EnrollmentTab studentId={detail.id} />,
              },
              {
                key: "order",
                label: "订单",
                children: <OrderTab studentId={detail.id} studentName={detail.name} />,
              },
              {
                key: "class",
                label: "班级",
                children: <ClassTab studentId={detail.id} />,
              },
              {
                key: "schedule",
                label: "课表",
                children: <ScheduleTab studentId={detail.id} />,
              },
              {
                key: "ledger",
                label: "账本",
                children: (
                  <LedgerTab
                    studentId={detail.id}
                    refreshToken={ledgerRefreshToken}
                  />
                ),
              },
            ]}
            data-testid="detail-tabs"
          />
          <ManualTrialGrantModal
            studentId={detail.id}
            studentName={detail.name}
            studentNo={detail.studentNo}
            open={grantOpen}
            onClose={() => setGrantOpen(false)}
            onGranted={() => setLedgerRefreshToken((t) => t + 1)}
          />
        </>
      )}
    </Drawer>
  );
}
